/**
 * Zyg Soundboard PWA — Engineer Edition (WAV capture)
 * Records raw PCM via Web Audio API and encodes to WAV on stop,
 * bypassing the MediaRecorder/Opus path that introduced static.
 * Brand: #FF0050 pink, #0A1628 navy
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "https://app.zyglive.com/api";

const css = `
  :root {
    --bg: #0A1628; --surface: #0F1E35; --surface2: #162540;
    --accent: #FF0050; --accent-dim: rgba(255, 0, 80, 0.12);
    --text: #FFFFFF; --text-muted: rgba(255,255,255,0.5); --text-dim: rgba(255,255,255,0.2);
    --success: #34C759; --warn: #FF9500; --radius: 16px; --radius-sm: 10px;
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: var(--bg); color: var(--text); font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; min-height: 100vh; display: flex; align-items: center; justify-content: center; }
  .app { width: 100%; max-width: 440px; min-height: 100vh; padding: 40px 24px 32px; display: flex; flex-direction: column; }
  .logo { font-size: 32px; font-weight: 800; color: var(--accent); letter-spacing: -1px; margin-bottom: 2px; }
  .logo-sub { font-size: 11px; color: var(--text-muted); letter-spacing: 2.5px; text-transform: uppercase; margin-bottom: 36px; }
  .screen { flex: 1; display: flex; flex-direction: column; }
  h2 { font-size: 24px; font-weight: 700; margin-bottom: 8px; line-height: 1.2; }
  p.lead { color: var(--text-muted); font-size: 15px; line-height: 1.6; margin-bottom: 28px; }
  .card { background: var(--surface); border-radius: var(--radius); padding: 20px; margin-bottom: 16px; border: 1px solid rgba(255,255,255,0.06); }
  .card-label { font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--accent); margin-bottom: 6px; }
  .card-value { font-size: 22px; font-weight: 700; }
  .card-sub { font-size: 13px; color: var(--text-muted); margin-top: 4px; }
  input[type="text"] { width: 100%; background: var(--surface); border: 1.5px solid rgba(255,255,255,0.08); border-radius: var(--radius-sm); color: var(--text); font-size: 28px; font-weight: 700; letter-spacing: 8px; padding: 16px 20px; text-transform: uppercase; text-align: center; outline: none; transition: border-color 0.2s; margin-bottom: 16px; }
  input[type="text"]:focus { border-color: var(--accent); }
  input[type="text"]::placeholder { color: var(--text-dim); letter-spacing: 4px; font-size: 20px; }
  label.field-label { display: block; font-size: 11px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px; }
  select { width: 100%; background: var(--surface); border: 1.5px solid rgba(255,255,255,0.1); border-radius: var(--radius-sm); color: var(--text); font-size: 15px; padding: 14px 16px; outline: none; margin-bottom: 16px; -webkit-appearance: none; appearance: none; background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path d='M2 4l4 4 4-4' stroke='white' stroke-width='1.5' fill='none' opacity='0.5'/></svg>"); background-repeat: no-repeat; background-position: right 16px center; }
  select:focus { border-color: var(--accent); }
  button { width: 100%; background: var(--accent); color: white; border: none; border-radius: var(--radius-sm); padding: 16px; font-size: 17px; font-weight: 700; cursor: pointer; transition: opacity 0.15s, transform 0.1s; margin-bottom: 12px; }
  button:active { transform: scale(0.98); }
  button:disabled { opacity: 0.4; cursor: not-allowed; }
  button.secondary { background: transparent; border: 1.5px solid rgba(255,255,255,0.15); color: var(--text-muted); font-size: 15px; }
  button.success-btn { background: var(--success); }
  .error-msg { background: rgba(255,0,80,0.1); border: 1px solid rgba(255,0,80,0.25); border-radius: var(--radius-sm); color: var(--accent); padding: 12px 16px; font-size: 14px; margin-bottom: 16px; line-height: 1.4; }
  .meter-wrap { margin-bottom: 16px; }
  .meter-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 8px; }
  .meter-title { font-size: 11px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-muted); }
  .meter-status { font-size: 12px; font-weight: 700; }
  .meter-track { width: 100%; height: 14px; background: var(--surface2); border-radius: 7px; overflow: hidden; position: relative; }
  .meter-fill { height: 100%; border-radius: 7px; transition: width 0.06s linear; }
  .meter-scale { display: flex; justify-content: space-between; margin-top: 4px; }
  .meter-scale span { font-size: 9px; color: var(--text-dim); }
  .recording-ring { width: 110px; height: 110px; border-radius: 50%; background: rgba(255,0,80,0.1); border: 2.5px solid var(--accent); display: flex; align-items: center; justify-content: center; margin: 8px auto 20px; animation: pulse 1.5s ease-in-out infinite; cursor: pointer; transition: transform 0.1s; }
  .recording-ring:active { transform: scale(0.95); }
  .recording-ring.idle { background: var(--accent-dim); animation: none; }
  @keyframes pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(255,0,80,0.35);} 50% { box-shadow: 0 0 0 16px rgba(255,0,80,0);} }
  .record-icon { width: 44px; height: 44px; border-radius: 50%; background: var(--accent); }
  .stop-icon { width: 34px; height: 34px; border-radius: 6px; background: var(--accent); }
  .duration { text-align: center; font-size: 42px; font-weight: 700; font-variant-numeric: tabular-nums; letter-spacing: 2px; margin-bottom: 4px; color: var(--accent); }
  .duration.idle { color: var(--text-muted); }
  .progress-bar { width: 100%; height: 4px; background: rgba(255,255,255,0.08); border-radius: 2px; overflow: hidden; margin-bottom: 8px; }
  .progress-fill { height: 100%; background: var(--accent); border-radius: 2px; transition: width 0.3s; }
  .progress-pct { text-align: center; color: var(--accent); font-weight: 700; font-size: 15px; margin-bottom: 8px; }
  .success-check { font-size: 64px; text-align: center; margin-bottom: 16px; }
  .hint { font-size: 13px; color: var(--text-dim); text-align: center; line-height: 1.5; margin-top: 8px; }
  .spacer { flex: 1; }
  .guide { background: var(--surface); border-radius: var(--radius); padding: 16px 18px; margin-bottom: 16px; border: 1px solid rgba(255,255,255,0.06); }
  .guide summary { font-size: 13px; font-weight: 600; color: var(--text-muted); cursor: pointer; list-style: none; }
  .guide summary::-webkit-details-marker { display: none; }
  .guide summary::before { content: "▸ "; color: var(--accent); }
  .guide[open] summary::before { content: "▾ "; }
  .guide-body { font-size: 13px; color: var(--text-muted); line-height: 1.7; margin-top: 12px; }
  .guide-body strong { color: var(--text); }
  .src-confirm { display: flex; align-items: center; gap: 8px; font-size: 13px; margin-bottom: 16px; padding: 10px 14px; border-radius: var(--radius-sm); }
  .src-confirm.ok { background: rgba(52,199,89,0.1); color: var(--success); }
  .src-confirm.wait { background: rgba(255,149,0,0.1); color: var(--warn); }
`;

async function verifyPairingCode(code) {
  const { data } = await axios.get(`${API_BASE}/sessions/by-code/${code}`);
  return data;
}
async function uploadSoundboardAudio(pairingCode, audioBlob, captureTimestampMs, onProgress) {
  const formData = new FormData();
  formData.append("pairing_code", pairingCode);
  formData.append("file", audioBlob, "soundboard_audio.wav");
  formData.append("capture_timestamp_ms", String(captureTimestampMs));
  await axios.post(`${API_BASE}/sessions/soundboard-upload`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 600000,
    onUploadProgress: (evt) => { if (onProgress && evt.total) onProgress(Math.round((evt.loaded * 100) / evt.total)); },
  });
}

// Encode interleaved Float32 channels to a 16-bit PCM WAV Blob
function encodeWAV(channelBuffers, sampleRate) {
  const numChannels = channelBuffers.length;
  const numFrames = channelBuffers[0].length;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const dataSize = numFrames * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  const writeStr = (off, s) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };

  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);            // PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeStr(36, "data");
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < numFrames; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      let s = Math.max(-1, Math.min(1, channelBuffers[ch][i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
      offset += 2;
    }
  }
  return new Blob([view], { type: "audio/wav" });
}

function PairingScreen({ onVerified }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const handleSubmit = async () => {
    const trimmed = code.trim().toUpperCase();
    if (trimmed.length < 4) return;
    setLoading(true); setError("");
    try { onVerified({ code: trimmed, session: await verifyPairingCode(trimmed) }); }
    catch { setError("Code not found. Check with the performer and try again."); setLoading(false); }
  };
  return (
    <div className="screen">
      <h2>Enter Pairing Code</h2>
      <p className="lead">Ask the performer for the 6-character code shown on their phone.</p>
      <input type="text" placeholder="XXXXXX" value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
        onKeyDown={(e) => e.key === "Enter" && handleSubmit()} maxLength={6} autoFocus
        autoComplete="off" autoCorrect="off" spellCheck={false} />
      {error && <div className="error-msg">{error}</div>}
      <button onClick={handleSubmit} disabled={loading || code.length < 4}>{loading ? "Verifying..." : "Connect"}</button>
      <p className="hint">No account needed. Works in any browser.</p>
    </div>
  );
}

function SetupScreen({ pairedData, onReady }) {
  const { code, session } = pairedData;
  const [devices, setDevices] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [error, setError] = useState("");
  const [level, setLevel] = useState(0);
  const [peak, setPeak] = useState(0);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);

  const loadDevices = useCallback(async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      const inputs = all.filter((d) => d.kind === "audioinput");
      setDevices(inputs);
      if (inputs.length && !selectedId) setSelectedId(inputs[0].deviceId);
    } catch { setError("Could not list audio inputs."); }
  }, [selectedId]);

  const startMonitor = useCallback(async (deviceId) => {
    stopMonitor();
    try {
      const constraints = { audio: deviceId
        ? { deviceId: { exact: deviceId }, echoCancellation:false, noiseSuppression:false, autoGainControl:false }
        : { echoCancellation:false, noiseSuppression:false, autoGainControl:false } };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      let smoothed = 0, peakDecay = 0;
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let sumSq = 0;
        for (let i = 0; i < data.length; i++) { const v = (data[i]-128)/128; sumSq += v*v; }
        const rms = Math.sqrt(sumSq / data.length);
        smoothed = smoothed*0.8 + rms*0.2;
        setLevel(Math.min(1, smoothed*3));
        peakDecay = Math.max(peakDecay*0.95, rms);
        setPeak(Math.min(1, peakDecay*3));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
      loadDevices();
    } catch { setError("Could not open that input. Pick a different device or check the cable/interface."); }
  }, [loadDevices]);

  const stopMonitor = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (audioCtxRef.current) { audioCtxRef.current.close().catch(()=>{}); audioCtxRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t)=>t.stop()); streamRef.current = null; }
  };

  useEffect(() => { startMonitor(""); return () => stopMonitor(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (selectedId) startMonitor(selectedId); /* eslint-disable-next-line */ }, [selectedId]);

  const hasSignal = peak > 0.04;
  const meterColor = level > 0.85 ? "var(--warn)" : "var(--accent)";
  const proceed = () => { stopMonitor(); onReady({ deviceId: selectedId }); };

  return (
    <div className="screen">
      <h2>Audio Input</h2>
      <p className="lead">Select the input carrying your board feed, then confirm signal on the meter before recording.</p>
      <label className="field-label">Input Device</label>
      <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
        {devices.length === 0 && <option>Loading inputs…</option>}
        {devices.map((d, i) => (<option key={d.deviceId || i} value={d.deviceId}>{d.label || `Input ${i+1}`}</option>))}
      </select>
      <div className="meter-wrap">
        <div className="meter-head">
          <span className="meter-title">Input Level</span>
          <span className="meter-status" style={{ color: hasSignal ? "var(--success)" : "var(--warn)" }}>{hasSignal ? "● SIGNAL DETECTED" : "○ NO SIGNAL"}</span>
        </div>
        <div className="meter-track"><div className="meter-fill" style={{ width: `${Math.round(level*100)}%`, background: meterColor }} /></div>
        <div className="meter-scale"><span>-∞</span><span>-12dB</span><span>0dB</span></div>
      </div>
      <div className={`src-confirm ${hasSignal ? "ok" : "wait"}`}>{hasSignal ? "Signal is coming in. You're ready to record." : "Play audio through the board — watch for the meter to move."}</div>
      {error && <div className="error-msg">{error}</div>}
      <details className="guide">
        <summary>How to connect a board feed</summary>
        <div className="guide-body">
          Take a line-level output from the console — an <strong>aux send</strong>, <strong>monitor out</strong>, or <strong>record/booth out</strong> works best.<br/><br/>
          Run it into a <strong>USB audio interface</strong>, then USB into this device. Pick that interface in the dropdown above. If the meter stays flat, check the cable, the interface's input gain, and that the board output is active.
        </div>
      </details>
      <div className="card">
        <div className="card-label">Paired Session</div>
        <div className="card-value">{code}</div>
        {session.performance_name && <div className="card-sub">{session.performance_name}</div>}
      </div>
      <button onClick={proceed} disabled={!selectedId}>Continue to Recording</button>
    </div>
  );
}

function RecordScreen({ pairedData, inputConfig, onComplete, onBack }) {
  const { code, session } = pairedData;
  const [mode, setMode] = useState("ready");
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [captureTimestampMs, setCaptureTimestampMs] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [level, setLevel] = useState(0);

  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const timerRef = useRef(null);
  const recBuffersRef = useRef([[], []]); // L, R
  const recLenRef = useRef(0);
  const sampleRateRef = useRef(44100);
  const recordingRef = useRef(false);

  const formatDuration = (s) => `${Math.floor(s/60).toString().padStart(2,"0")}:${(s%60).toString().padStart(2,"0")}`;

  const startRecording = async () => {
    setError("");
    try {
      const constraints = { audio: inputConfig.deviceId
        ? { deviceId: { exact: inputConfig.deviceId }, echoCancellation:false, noiseSuppression:false, autoGainControl:false }
        : { echoCancellation:false, noiseSuppression:false, autoGainControl:false } };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      sampleRateRef.current = ctx.sampleRate;
      const source = ctx.createMediaStreamSource(stream);
      sourceRef.current = source;

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyserRef.current = analyser;
      source.connect(analyser);

      // ScriptProcessor captures raw PCM frames (widely supported, reliable)
      const bufferSize = 4096;
      const processor = ctx.createScriptProcessor(bufferSize, 2, 2);
      processorRef.current = processor;
      recBuffersRef.current = [[], []];
      recLenRef.current = 0;
      recordingRef.current = true;

      processor.onaudioprocess = (e) => {
        if (!recordingRef.current) return;
        const inBuf = e.inputBuffer;
        const chCount = inBuf.numberOfChannels;
        const L = inBuf.getChannelData(0);
        const R = chCount > 1 ? inBuf.getChannelData(1) : inBuf.getChannelData(0);
        recBuffersRef.current[0].push(new Float32Array(L));
        recBuffersRef.current[1].push(new Float32Array(R));
        recLenRef.current += L.length;
      };

      source.connect(processor);
      processor.connect(ctx.destination); // required for processor to run

      // level meter
      const data = new Uint8Array(analyser.frequencyBinCount);
      let smoothed = 0;
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let sumSq = 0;
        for (let i=0;i<data.length;i++){ const v=(data[i]-128)/128; sumSq+=v*v; }
        smoothed = smoothed*0.8 + Math.sqrt(sumSq/data.length)*0.2;
        setLevel(Math.min(1, smoothed*3));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();

      setCaptureTimestampMs(Date.now());
      setMode("recording");
      setDuration(0);
      timerRef.current = setInterval(() => setDuration((d)=>d+1), 1000);
    } catch (err) {
      setError("Could not start recording from the selected input. Go back and reselect the device.");
    }
  };

  const flattenChannel = (chunks, totalLen) => {
    const out = new Float32Array(totalLen);
    let offset = 0;
    for (const c of chunks) { out.set(c, offset); offset += c.length; }
    return out;
  };

  const stopRecording = () => {
    if (mode !== "recording") return;
    recordingRef.current = false;
    if (timerRef.current) clearInterval(timerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    try {
      if (processorRef.current) { processorRef.current.disconnect(); processorRef.current.onaudioprocess = null; }
      if (sourceRef.current) sourceRef.current.disconnect();
    } catch {}
    const L = flattenChannel(recBuffersRef.current[0], recLenRef.current);
    const R = flattenChannel(recBuffersRef.current[1], recLenRef.current);
    const wav = encodeWAV([L, R], sampleRateRef.current);
    setAudioBlob(wav);
    if (streamRef.current) streamRef.current.getTracks().forEach((t)=>t.stop());
    if (audioCtxRef.current) { audioCtxRef.current.close().catch(()=>{}); audioCtxRef.current = null; }
    setLevel(0);
    setMode("stopped");
  };

  const handleUpload = async () => {
    if (!audioBlob) return;
    setUploading(true); setError("");
    try { await uploadSoundboardAudio(code, audioBlob, captureTimestampMs, setUploadProgress); onComplete(); }
    catch { setError("Upload failed. Check your connection and try again."); setUploading(false); }
  };

  const reset = () => { setMode("ready"); setAudioBlob(null); setDuration(0); setCaptureTimestampMs(null); setError(""); setUploadProgress(0); };

  useEffect(() => () => {
    recordingRef.current = false;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    if (audioCtxRef.current) audioCtxRef.current.close().catch(()=>{});
    if (streamRef.current) streamRef.current.getTracks().forEach(t=>t.stop());
  }, []);

  if (uploading) {
    return (
      <div className="screen">
        <h2>Uploading</h2>
        <p className="lead">Sending the board recording to Zyg…</p>
        <div className="progress-bar"><div className="progress-fill" style={{ width: `${uploadProgress}%` }} /></div>
        <div className="progress-pct">{uploadProgress}%</div>
        <p className="hint">Keep this tab open until upload completes. WAV files are larger — this can take a moment.</p>
      </div>
    );
  }
  if (mode === "stopped") {
    return (
      <div className="screen">
        <h2>Ready to Upload</h2>
        <p className="lead">Recorded {formatDuration(duration)} from the board feed (uncompressed WAV).</p>
        <div className="card">
          <div className="card-label">Session</div>
          <div className="card-value">{code}</div>
          {session.performance_name && <div className="card-sub">{session.performance_name}</div>}
        </div>
        {error && <div className="error-msg">{error}</div>}
        <button className="success-btn" onClick={handleUpload}>Upload Recording</button>
        <button className="secondary" onClick={reset}>Discard &amp; Re-record</button>
      </div>
    );
  }
  if (mode === "recording") {
    return (
      <div className="screen">
        <h2>Recording</h2>
        <p className="lead">Capturing the full show from the board feed.</p>
        <div className="duration">{formatDuration(duration)}</div>
        <div className="meter-wrap"><div className="meter-track"><div className="meter-fill" style={{ width: `${Math.round(level*100)}%`, background: level>0.85?"var(--warn)":"var(--accent)" }} /></div></div>
        <div className="recording-ring" onClick={stopRecording}><div className="stop-icon" /></div>
        <p style={{ textAlign:"center", color:"rgba(255,255,255,0.4)", marginBottom:24 }}>Tap to stop</p>
        <div className="card"><div className="card-label">Paired Session</div><div className="card-value">{code}</div></div>
      </div>
    );
  }
  return (
    <div className="screen">
      <h2>Ready</h2>
      <p className="lead">Start recording when the performance begins. Let it run for the whole show — each performer's clip syncs to its matching moment.</p>
      {error && <div className="error-msg">{error}</div>}
      <div className="recording-ring idle" onClick={startRecording}><div className="record-icon" /></div>
      <button onClick={startRecording}>Start Recording</button>
      <button className="secondary" onClick={onBack}>Change Input</button>
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-label">Paired Session</div>
        <div className="card-value">{code}</div>
        {session.performance_name && <div className="card-sub">{session.performance_name}</div>}
      </div>
    </div>
  );
}

function DoneScreen({ onReset }) {
  return (
    <div className="screen">
      <div className="spacer" />
      <div className="success-check">✅</div>
      <h2 style={{ textAlign:"center", marginBottom:12 }}>Upload Complete</h2>
      <p className="lead" style={{ textAlign:"center" }}>The board recording is in. Performers' videos will sync to it automatically.</p>
      <div className="spacer" />
      <button className="secondary" onClick={onReset}>New Session</button>
    </div>
  );
}

function App() {
  const [screen, setScreen] = useState("pairing");
  const [pairedData, setPairedData] = useState(null);
  const [inputConfig, setInputConfig] = useState(null);
  return (
    <>
      <style>{css}</style>
      <div className="app">
        <div className="logo">zyg</div>
        <div className="logo-sub">Soundboard · Engineer</div>
        {screen === "pairing" && <PairingScreen onVerified={(d) => { setPairedData(d); setScreen("setup"); }} />}
        {screen === "setup" && <SetupScreen pairedData={pairedData} onReady={(cfg) => { setInputConfig(cfg); setScreen("record"); }} />}
        {screen === "record" && <RecordScreen pairedData={pairedData} inputConfig={inputConfig} onComplete={() => setScreen("done")} onBack={() => setScreen("setup")} />}
        {screen === "done" && <DoneScreen onReset={() => { setPairedData(null); setInputConfig(null); setScreen("pairing"); }} />}
      </div>
    </>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<App />);
